import React from 'react';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { GuideTooltip } from './GuideTooltip';

type ColorVariant = 'indigo' | 'yellow' | 'teal' | 'rose' | 'blue' | 'emerald';

interface StatsCardProps {
  title: string;
  tooltipTerm?: string; // Optional stable key for translation lookup
  value: string | number;
  trend?: string;
  trendDirection?: 'up' | 'down';
  trendLabel?: string;
  icon?: React.ReactNode;
  variant?: ColorVariant;
  className?: string;
}

export const StatsCard: React.FC<StatsCardProps> = ({ 
  title, 
  tooltipTerm,
  value, 
  trend,
  trendDirection = 'up',
  trendLabel = 'this month',
  icon, 
  variant = 'indigo',
  className 
}) => {
  
  const colorStyles = {
    indigo: { 
      bg: 'bg-indigo-50 dark:bg-indigo-900/20', 
      text: 'text-indigo-600 dark:text-indigo-400',
      iconBg: 'bg-indigo-50 dark:bg-indigo-900/40',
      iconShadow: 'shadow-[0_0_10px_rgba(99,102,241,0.25)] dark:shadow-[0_0_10px_rgba(129,140,248,0.15)]'
    },
    yellow: { 
      bg: 'bg-amber-50 dark:bg-amber-900/20', 
      text: 'text-amber-600 dark:text-amber-400',
      iconBg: 'bg-amber-50 dark:bg-amber-900/40',
      iconShadow: 'shadow-[0_0_10px_rgba(245,158,11,0.25)] dark:shadow-[0_0_10px_rgba(251,191,36,0.15)]'
    },
    teal: { 
      bg: 'bg-teal-50 dark:bg-teal-900/20', 
      text: 'text-teal-600 dark:text-teal-400',
      iconBg: 'bg-teal-50 dark:bg-teal-900/40',
      iconShadow: 'shadow-[0_0_10px_rgba(13,148,136,0.25)] dark:shadow-[0_0_10px_rgba(45,212,191,0.15)]'
    },
    rose: { 
      bg: 'bg-rose-50 dark:bg-rose-900/20', 
      text: 'text-rose-600 dark:text-rose-400',
      iconBg: 'bg-rose-50 dark:bg-rose-900/40',
      iconShadow: 'shadow-[0_0_10px_rgba(225,29,72,0.25)] dark:shadow-[0_0_10px_rgba(251,113,133,0.15)]'
    },
    blue: { 
      bg: 'bg-blue-50 dark:bg-blue-900/20', 
      text: 'text-blue-600 dark:text-blue-400',
      iconBg: 'bg-blue-50 dark:bg-blue-900/40',
      iconShadow: 'shadow-[0_0_10px_rgba(37,99,235,0.25)] dark:shadow-[0_0_10px_rgba(96,165,250,0.15)]'
    },
    emerald: { 
      bg: 'bg-emerald-50 dark:bg-emerald-900/20', 
      text: 'text-emerald-600 dark:text-emerald-400',
      iconBg: 'bg-emerald-50 dark:bg-emerald-900/40',
      iconShadow: 'shadow-[0_0_10px_rgba(5,150,105,0.25)] dark:shadow-[0_0_10px_rgba(52,211,153,0.15)]'
    }
  };

  const colors = colorStyles[variant] || colorStyles.indigo;

  return (
    <div className={`bg-white dark:bg-slate-900 p-6 rounded-3xl border border-slate-100 dark:border-slate-800 flex items-start justify-between h-full transition-all hover:-translate-y-1 hover:shadow-[0_20px_40px_-15px_rgba(0,0,0,0.1)] shadow-[0_0_20px_-5px_rgba(0,0,0,0.05)] dark:shadow-[0_0_20px_-5px_rgba(255,255,255,0.03)] ${className || ''}`}>
      
      <div className="flex flex-col justify-between h-full gap-4">
        <div>
          <div className="mb-1">
            <GuideTooltip term={tooltipTerm || title}>
              <p className="text-sm font-bold text-slate-600 dark:text-slate-400">{title}</p>
            </GuideTooltip>
          </div>
          <h3 className="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">{value}</h3>
        </div>
        
        {trend && (
          <div className="flex items-center gap-2">
            <span className={`text-xs font-bold px-2 py-1 rounded-md flex items-center gap-1 ${
              trendDirection === 'up' 
                ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' 
                : 'bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400'
            }`}>
              {trendDirection === 'up' ? <ArrowUpRight size={12} strokeWidth={3} /> : <ArrowDownRight size={12} strokeWidth={3} />}
              {trend}
            </span>
            <span className="text-xs text-slate-400 font-medium">{trendLabel}</span>
          </div>
        )}
      </div>

      {icon && (
        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${colors.iconBg} ${colors.text} ${colors.iconShadow}`}>
          {React.isValidElement(icon) ? React.cloneElement(icon as React.ReactElement<any>, { size: 24, strokeWidth: 2.5 }) : icon}
        </div>
      )}
    </div>
  );
};